import { Link } from "react-router-dom";
import { PUBLIC_ROUTES } from "../../routes";
import MarketingShell from "./MarketingShell";

const SUPPORT = [
  {
    title: "Learn the job in a twin, not on the line",
    body: "RoboTwins turns a phone walkthrough of the cell into a physics-accurate world twin, then teaches the policy there — across thousands of randomised variations — before a real arm ever moves.",
  },
  {
    title: "Catch the crash before it happens",
    body: "Every collision, every overreach, every fixture clipped happens in simulation, where it costs nothing. Only a validated policy, inside a checked safety envelope, is allowed onto the hardware.",
  },
  {
    title: "Deploy to the robots you already own",
    body: "RoboTwins drives UR, ABB, FANUC and the rest through the same runtime — with the on-device safety reflex and the signed change record that make a line manager comfortable switching it on.",
  },
] as const;

const STORY_BULLETS = [
  "Devin, 4:30pm Friday",
  "Line changeover for a new product",
  "Used to mean 40 hours of CAD and on-floor tuning",
  "Wants the line running Monday morning",
] as const;

export default function RoboTwins() {
  return (
    <MarketingShell pageClass="mk-care-page mk-robotwins">
      <section className="mk-care-hero">
        <p className="sg-eyebrow">Industrial · RoboTwins</p>
        <h1>The intelligence that makes a factory robot safe to retask in a weekend.</h1>
        <p className="mk-care-lede">
          RoboTwins is where this all started — the world-model
          platform that lets integrators train, validate, and deploy
          robot behaviours in a high-fidelity twin before touching the
          real cell. The same trust layer that gets a humanoid into a
          home gets an arm onto a production line: tested in sim,
          safe on the bus, signed in the ledger. We don&rsquo;t build
          the robot. We make the part the plant manager has to trust.
        </p>
        <div className="sg-hero-actions">
          <Link to={PUBLIC_ROUTES.contact} className="sg-btn sg-btn-primary">
            Bring RoboTwins to your line
          </Link>
          <Link to={PUBLIC_ROUTES.howItWorks} className="sg-btn sg-btn-secondary">
            How it works
          </Link>
        </div>
      </section>

      <div className="mk-care-art">
        <figure className="mk-art-frame">
          <div className="mk-art-bar" aria-hidden="true">
            <span>mk-vision // world-twin build</span>
            <span><em>training in sim</em></span>
          </div>
          <img
            src="/ascii-industrial.png"
            alt="Two industrial robot arms working a conveyor line in a moonlit factory hall, rendered in ASCII characters — labour as the RoboTwins world model studies it"
          />
          <div className="mk-art-bar bottom" aria-hidden="true">
            <span><em>✓</em> policy validated</span>
            <span>cell 07 · friday 4:30pm</span>
            <span><strong>0 crashes on floor</strong></span>
          </div>
        </figure>
      </div>

      <section className="mk-story">
        <div className="mk-story-card">
          <p className="sg-section-kicker">A real Friday</p>
          <h2>Devin&rsquo;s changeover.</h2>
          <ul className="mk-story-bullets">
            {STORY_BULLETS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>
            Devin walks the cell with his phone for five minutes.
            RoboTwins builds the twin overnight, randomises the
            lighting and the bin positions, and trains the new pick
            policy against thousands of variations while the plant is
            dark. Nothing on the floor moves; nothing gets bent. By
            Monday the policy has been validated inside the safety
            envelope, the change is signed in the ledger, and the arm
            runs the new product on the first shift — with the
            retuning that used to eat his week already done in
            simulation.
          </p>
        </div>
      </section>

      <section className="sg-pillars">
        <div className="sg-section-heading compact">
          <p className="sg-section-kicker">What RoboTwins does</p>
          <h2>Three things that change how a cell gets built.</h2>
        </div>
        <div className="sg-pillars-grid">
          {SUPPORT.map((item) => (
            <article className="sg-pillar" key={item.title}>
              <h3 dangerouslySetInnerHTML={{ __html: item.title }} />
              <p dangerouslySetInnerHTML={{ __html: item.body }} />
            </article>
          ))}
        </div>
      </section>

      <section className="mk-reassure">
        <p className="sg-section-kicker">What RoboTwins isn&rsquo;t</p>
        <h2>RoboTwins isn&rsquo;t a robot, and it isn&rsquo;t a black box.</h2>
        <p>
          We don&rsquo;t manufacture the arm; we provide the world
          twin, the trained policy, the safety envelope, and the
          signed record of every change. The world twin is always
          slightly off from reality — so RoboTwins assumes it, trains
          with heavy domain randomisation, and never lets a policy
          onto hardware until the floor is proven solid and the
          envelope holds. Every deployment is auditable end to end.
        </p>
        <div className="sg-hero-actions center">
          <Link to={PUBLIC_ROUTES.contact} className="sg-btn sg-btn-primary">
            Request a pilot
          </Link>
          <Link to={PUBLIC_ROUTES.maternal} className="sg-btn sg-btn-secondary">
            The principle behind it
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
